import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle2, Sparkles, FileMusic, HelpCircle } from "lucide-react";
import { api, fmtError } from "../lib/api";
import { SEO } from "../components/SEO";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";

const TOPICS = [
  { value: "Exclusivité d'un morceau", icon: Sparkles, desc: "Retirer un morceau du catalogue pour vous seul" },
  { value: "Musique sur-mesure", icon: FileMusic, desc: "Une composition originale pour votre projet" },
  { value: "Question licence", icon: HelpCircle, desc: "Un doute sur les droits ou l'usage" },
  { value: "Autre demande", icon: Mail, desc: "Partenariat, presse, autre sujet" },
];

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", org: "", topic: TOPICS[0].value, message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-zinc-500";

  return (
    <main data-testid="contact-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8">
      <SEO title="Contact" path="/contact"
           description="Une question sur une licence, un projet de musique sur mesure, un partenariat ? Écrivez à La Console des Artistes, réponse rapide." />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Contact</p>
        <h1 className="mt-3 max-w-3xl font-display text-4xl font-black tracking-tighter sm:text-6xl">
          Exclusivité, sur-mesure, parlons-en.
        </h1>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
          Vous êtes une marque, un réalisateur, un producteur ou un créateur de contenu ?
          Dites-nous ce qu'il vous faut — réponse sous 48 h ouvrées.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-5">
        <Reveal delay={0.05}>
          <div className="space-y-3 lg:col-span-2">
            {TOPICS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setForm({ ...form, topic: t.value })}
                data-testid={`contact-topic-${t.value === TOPICS[0].value ? "exclusivite" : t.value === TOPICS[1].value ? "surmesure" : t.value === TOPICS[2].value ? "licence" : "autre"}`}
                className={`flex w-full items-start gap-4 border p-5 text-left transition-colors ${
                  form.topic === t.value ? "border-amber-500 bg-amber-500/5" : "border-white/[0.08] hover:border-amber-500/40"
                }`}
              >
                <t.icon className={`mt-0.5 h-5 w-5 shrink-0 ${form.topic === t.value ? "text-amber-400" : "text-zinc-500"}`} />
                <span>
                  <span className="block text-sm font-medium text-zinc-100">{t.value}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-zinc-500">{t.desc}</span>
                </span>
              </button>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center border border-amber-500/30 bg-amber-500/5 p-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-amber-400" />
              <p className="mt-5 font-display text-2xl font-medium">Message bien reçu</p>
              <p className="mt-3 max-w-sm text-sm leading-relaxed text-zinc-400">
                Merci {form.name} — votre demande « {form.topic} » est entre nos mains.
                Nous revenons vers vous sous 48 h ouvrées à {form.email}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 border border-white/[0.08] bg-surface p-8" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Votre nom</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className={inputCls} placeholder="Jean Dupont" />
                </div>
                <div>
                  <label className={labelCls}>Votre email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className={inputCls} placeholder="jean@marque.com" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Société / structure (optionnel)</label>
                <input value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} data-testid="contact-org" className={inputCls} placeholder="Nom de la marque, de la production…" />
              </div>
              <input
                type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
              />
              <div>
                <label className={labelCls}>Votre message</label>
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" className={inputCls} placeholder="Décrivez votre projet, vos délais, le morceau concerné…" />
              </div>
              <button
                type="submit" disabled={busy} data-testid="contact-submit"
                className="flex w-full items-center justify-center gap-3 bg-amber-500 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {busy ? "Envoi en cours…" : "Envoyer ma demande"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </main>
  );
};

export default Contact;
